/* Shared by the quiz and both cart surfaces. No client secrets. Needs quiz-form.js first. */
(() => {
  const config = window.__MOSES_QUIZ__;
  if (!config || !window.MosesQuizForm) return;
  const key = "moses_quiz_v1";
  const UTM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];
  const ATTRIBUTION_KEYS = [...UTM_KEYS, "campaign_id", "adset_id", "ad_id", "campaign_name", "adset_name", "ad_name", "placement", "utm_id"];
  const HANDOFF_KEYS = [
    "quiz_source",
    "order_id",
    "onboarding_token",
    "onboarding_url",
  ];
  let memory = {};
  const read = () => {
    try {
      return { ...memory, ...JSON.parse(sessionStorage.getItem(key) || "{}") };
    } catch {
      return memory;
    }
  };
  const save = (patch) => {
    memory = { ...read(), ...patch };
    try {
      sessionStorage.setItem(key, JSON.stringify(memory));
    } catch {
      /* in-memory fallback */
    }
    document.dispatchEvent(new CustomEvent("quiz:changed"));
    return memory;
  };
  const uuid = () => crypto.randomUUID();
  const localUrl = (value, fallback = config.productUrl) => {
    if (value == null || value === "") return fallback;
    try {
      const url = new URL(value, location.origin);
      return url.origin === location.origin
        ? url.pathname + url.search + url.hash
        : fallback;
    } catch {
      return fallback;
    }
  };
  const qualified = () => {
    const state = read();
    return (
      state.qualified === true &&
      Boolean(state.attributes?.order_id && state.attributes?.onboarding_token)
    );
  };
  const isRetainer = (item) =>
    Boolean(config.productId) &&
    String(item.product_id ?? item.productId) === String(config.productId);
  const track = (name, data = {}) => {
    // Shopify's existing analytics subscribers own delivery and consent. No second pixel loader.
    try {
      const attr = attribution();
      const utms = Object.fromEntries(UTM_KEYS.filter(key => attr[key]).map(key => [key, attr[key]]));
      window.MosesQuizForm.track(name, {
        ...window.__MOSES_ANALYTICS__,
        ...utms,
        path: location.pathname,
        funnel_variant: read().funnelVariant || (read().emailGate ? "cleaner-retainer" : "storefront"),
        event_id: uuid(),
        ...data,
      });
    } catch {
      /* non-blocking */
    }
  };
  // Replace unrelated campaigns as one record; only the exact same ad may retain its known fields.
  const ATTR_KEY = "moses_attribution";
  const TOUCH_TTL = 30 * 24 * 60 * 60 * 1000;
  let attributionMemory = {};
  let capturedUrl;
  const freshTouch = (touch) => {
    const age = Date.now() - Date.parse(touch?.ts);
    return age >= 0 && age < TOUCH_TTL;
  };
  const valuesFromUrl = (url) => Object.fromEntries(ATTRIBUTION_KEYS
    .map(name => [name, (url.searchParams.get(name) || "").trim().slice(0, 200)])
    .filter(([, value]) => value));
  const safeTouch = (url, ts = new Date().toISOString(), referrer = document.referrer) => {
    const values = valuesFromUrl(url);
    const clean = new URL(url.origin + url.pathname);
    for (const [name, value] of Object.entries(values)) clean.searchParams.set(name, value);
    let referringOrigin = "";
    try { referringOrigin = new URL(referrer).origin; } catch { /* optional */ }
    return { url: clean.href, referrer: referringOrigin, ts, values };
  };
  const sameAd = (previous, current) => {
    const before = previous?.values || {};
    const after = current.values;
    // A shared campaign/ad set alone cannot identify the creative. Reject inconsistent IDs too.
    return /^\d+$/.test(after.ad_id || "") && after.ad_id === before.ad_id &&
      ["campaign_id", "adset_id", "utm_id"].every(name =>
        !before[name] || !after[name] || before[name] === after[name]);
  };
  const attribution = () => {
    let attr = attributionMemory;
    // Read the newest complete record, including the tab fallback when durable writes fail.
    for (const storage of [() => localStorage, () => sessionStorage]) {
      try {
        const candidate = JSON.parse(storage().getItem(ATTR_KEY) || "null");
        if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
          const candidateTime = Date.parse(candidate.last_touch?.ts) || 0;
          const currentTime = Date.parse(attr.last_touch?.ts) || 0;
          if ((candidate.version === 3 && attr.version !== 3) ||
              (candidate.version === attr.version && candidateTime >= currentTime) ||
              !Object.keys(attr).length) attr = candidate;
        }
      } catch { /* optional */ }
    }
    if (attr.version !== 3) {
      // Legacy flat values may already be mixed. Only migrate an actual tagged URL.
      let first;
      let latest;
      for (const [name, touch] of [["first", attr.first_touch], ["last", attr.last_touch]]) {
        try {
          if (!freshTouch(touch)) continue;
          const rebuilt = safeTouch(new URL(touch.url), touch.ts, touch.referrer);
          if (name === "first") first = rebuilt;
          if (Object.keys(rebuilt.values).length) latest = rebuilt;
        } catch { /* legacy data is untrusted */ }
      }
      attr = { version: 3, anon_id: attr.anon_id || uuid(), first_touch: first, campaign_touch: latest };
    }
    const url = new URL(location.href);
    const touch = safeTouch(url);
    if (!freshTouch(attr.first_touch)) attr.first_touch = touch;
    if (!freshTouch(attr.campaign_touch)) delete attr.campaign_touch;
    // ID-only links must not erase names captured for this exact ad. Other tagged visits replace all fields.
    if (capturedUrl !== url.href && Object.keys(touch.values).length) {
      const previous = attr.campaign_touch;
      if ((capturedUrl !== undefined || touch.referrer === url.origin) && sameAd(previous, touch)) {
        const retained = Object.keys(previous.values).filter(name => !touch.values[name]);
        attr.campaign_touch = retained.length ? {
          ...touch,
          values: { ...previous.values, ...touch.values },
          retained_fields: retained,
          retained_from_at: previous.retained_from_at || previous.ts,
        } : touch;
      } else attr.campaign_touch = touch;
    }
    capturedUrl = url.href;
    attr.last_touch = touch;
    for (const name of ATTRIBUTION_KEYS) delete attr[name];
    Object.assign(attr, attr.campaign_touch?.values || {});
    attributionMemory = attr;
    for (const storage of [() => localStorage, () => sessionStorage]) {
      try { storage().setItem(ATTR_KEY, JSON.stringify(attr)); } catch { /* optional */ }
    }
    const cookies = Object.fromEntries(
      document.cookie.split("; ").map((c) => c.split(/=(.*)/s).slice(0, 2)),
    );
    // Read Meta identifiers independently; they are not campaign-name evidence.
    const result = { ...attr };
    if (cookies._fbp) result.fbp = cookies._fbp;
    if (cookies._fbc) result.fbc = cookies._fbc;
    return result;
  };
  attribution();
  // The shared transport (quiz-form.js) plus the quiz's attribution and event metadata.
  function post(path, body, eventId) {
    let posthogId = {};
    try {
      const id = localStorage.getItem("moses_posthog_distinct_id");
      if (id) posthogId = { posthog_distinct_id: id };
    } catch { /* Attribution storage is optional. */ }
    const matching = window.MosesQuizForm.metaContext?.(eventId) || {};
    const attr = { ...attribution(), ...posthogId };
    delete attr.fbp; delete attr.fbc;
    return window.MosesQuizForm.request(config.apiBase, path, {
      method: "POST",
      body: {
        ...body,
        attribution: { ...attr, ...matching.attribution },
        meta: {
          event_id: eventId,
          event_source_url: location.origin + location.pathname,
          ...matching.meta,
          test_event_code:
            new URLSearchParams(location.search).get("test_event_code") ||
            undefined,
        },
      },
    });
  }
  async function updateCart(body, errorMessage) {
    const response = await fetch(config.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(errorMessage);
    return response.json();
  }
  function showError(message, selector) {
    const store = window.Alpine?.store("cart");
    if (store) store.error = message;
    const feedback = selector && document.querySelector(selector);
    if (feedback) {
      feedback.textContent = message;
      feedback.hidden = false;
    }
  }
  const storageAvailable = () => {
    try {
      sessionStorage.setItem(key + "_probe", "1");
      sessionStorage.removeItem(key + "_probe");
      return true;
    } catch {
      return false;
    }
  };
  function start({ checkout = false, returnUrl = location.href, emailGate = false, url = config.url } = {}) {
    if (!storageAvailable()) {
      showError(
        "Please allow session storage in your browser to save your quiz answers before checkout.",
        "[data-quiz-cart-error]",
      );
      return;
    }
    save({
      checkoutIntent: checkout,
      returnUrl: localUrl(returnUrl),
      emailGate,
      pending: null,
      resume: false,
      ...(qualified()
        ? {}
        : { qualified: false, attributes: null, answers: {}, step: 0, screen: "question", eventId: null }),
    });
    location.assign(localUrl(url, "/pages/quiz"));
  }
  async function attachCart() {
    if (!qualified())
      throw new Error("Please complete the eligibility quiz first.");
    const attributes = { ...read().attributes };
    const attr = attribution();
    // Empty strings explicitly remove stale cart attributes in Shopify's Ajax API.
    for (const name of ATTRIBUTION_KEYS) {
      attributes[name] = attr[name] || "";
      attributes["first_touch_" + name] = attr.first_touch?.values?.[name] || "";
    }
    attributes.attribution_model = "last_tagged_visit_30d_v3";
    attributes.attribution_retained_fields = (attr.campaign_touch?.retained_fields || []).join(",");
    attributes.attribution_retained_from_at = attr.campaign_touch?.retained_from_at || "";
    attributes.attribution_utm_status = UTM_KEYS.every(name => attr[name]) ? "complete" :
      attr.campaign_touch ? "partial" : "untagged";
    attributes.attribution_touch_at = attr.campaign_touch?.ts || "";
    attributes.first_touch_at = attr.first_touch?.ts || "";
    for (const name of ["fbp", "fbc"]) if (attr[name]) attributes[name] = attr[name];
    if (typeof window.MosesRefreshGoogleIdentity === "function") {
      try { await window.MosesRefreshGoogleIdentity(); } catch { /* analytics is optional */ }
    }
    // Clear any previous cart identity, then attach the complete consented gtag tuple.
    for (const name of ["_ga_client_id", "_ga_session_id", "_ga_identity_source", "_ga_measurement_id"])
      attributes[name] = "";
    try {
      const ga = JSON.parse(sessionStorage.getItem("moses_ga4_session") || "{}");
      const analytics = window.__MOSES_STOREFRONT_ANALYTICS__ || {};
      const age = Date.now() - Number(ga.updated_at);
      if (analytics.ga4Enabled && ga.identity_source === "gtag" &&
          ga.measurement_id === analytics.ga4Id && age >= 0 && age < 30 * 60 * 1000 &&
          /^\d{1,20}\.\d{1,20}$/.test(String(ga.client_id || "")) &&
          /^\d{1,20}$/.test(String(ga.session_id || ""))) {
        attributes._ga_client_id = String(ga.client_id);
        attributes._ga_session_id = String(ga.session_id);
        attributes._ga_identity_source = "gtag";
        attributes._ga_measurement_id = ga.measurement_id;
      }
    } catch {
      /* optional: checkout must still work without analytics */
    }
    await updateCart(
      { attributes },
      "Unable to prepare your cart. Please try again.",
    );
  }
  async function clearCartHandoff(cart) {
    if (cart.items.some(isRetainer) || cart.attributes?.quiz_source !== "moses")
      return cart;
    const attributes = Object.fromEntries(
      HANDOFF_KEYS.map((name) => [name, ""]),
    );
    return updateCart(
      { attributes },
      "Unable to update your cart. Please try again.",
    );
  }
  async function proceedToCheckout(cart) {
    if (cart.items.some(isRetainer)) {
      if (!qualified()) {
        start({ checkout: true });
        return;
      }
      await attachCart();
    } else await clearCartHandoff(cart);
    location.assign(config.root + "checkout");
  }
  const api = {
    config,
    read,
    save,
    qualified,
    isRetainer,
    track,
    post,
    uuid,
    localUrl,
    start,
    attachCart,
    clearCartHandoff,
    storageAvailable,
  };
  window.MosesQuiz = api;

  // Delegated checkout interception covers the drawer and the standard cart form.
  let checkingOut = false;
  async function checkout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    const cartStore = window.Alpine?.store?.("cart");
    if (cartStore?.busy) { checkingOut = false; return; }
    try {
      let cart = await window.mosesCart.get();
      const form = event.target.closest("form[data-quiz-cart-form]");
      const updates = Object.fromEntries(
        [...(form?.querySelectorAll("[data-line-key]") || [])].map((input) => [
          input.dataset.lineKey,
          Number(input.value),
        ]),
      );
      if (
        Object.values(updates).some(
          (quantity) => !Number.isInteger(quantity) || quantity < 0,
        )
      )
        throw new Error("Please enter a whole quantity of zero or more.");
      if (Object.keys(updates).length)
        cart = await updateCart(
          { updates },
          "Unable to update your cart. Please try again.",
        );
      if (cartStore?.reconcileWelcomeCases) {
        try {
          cart = await cartStore.reconcileWelcomeCases(cart);
          cartStore.setCart(cart);
        } catch (error) { cartStore.error = error.message; throw error; }
      }
      if (document.querySelector('[data-cart-drawer]') && cartStore) {
        cartStore.setCart(cart);
        cartStore.open('cart');
      } else await proceedToCheckout(cart);
    } catch (error) {
      showError(error.message, "[data-quiz-cart-error]");
    } finally {
      checkingOut = false;
    }
  }
  function gateCheckout(event) {
    const control = event.target.closest("[data-quiz-checkout]");
    if (!control) return;
    void checkout(event);
  }
  document.addEventListener("click", gateCheckout, true);
  document.addEventListener(
    "submit",
    (event) => {
      if (event.submitter?.matches("[data-quiz-checkout]")) void checkout(event);
    },
    true,
  );
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-quiz-checkout]").forEach((control) => {
      control.disabled = false;
    });
  });
})();
